import {useRecoilState} from "recoil";
import {
    loadingState, isShopCopyState,
    spaceNameState,
    spaceState
} from "@services/stores/storeState";
import useLazyQuery from "@utils/useLazyQuery";
import {KEYS} from "@app/constants/keys";
import {
    AllSpaceBrandShopListGetPayload, ShopCopyEntity, ShopCopyPayload, ShopCopyProgressEntity, ShopCopyProgressPayload,
    SpaceAllListGetPayload,
    SpaceBrandGetPayload, SpaceBrandShopAllListGetPayload,
    SpaceEntity,
    SpaceGetPayload,
    SpaceListGetPayload,
    SpacePayload
} from "@services/entities/spaceEntity";
import {
    AllSpaceBrandShopListGetApi, ShopCopyApi, ShopCopyProgressApi,
    SpaceAllListGetApi,
    SpaceBrandGetApi, SpaceBrandShopAllListGetApi,
    SpaceDeleteApi,
    SpaceGetApi,
    SpaceListGetApi,
    SpacePostApi,
    SpacePutApi
} from "@services/dataSource/spaceApi";
import useCustomMutation from "@utils/useCustomMutation";
import {useQueries, useQueryClient} from "react-query";
import {DeletePayload} from "@services/entities/commonEntity";
import {ResponseEntity, ResponseListEntity} from "@services/entities/baseEntity";
import {ShopCopySaveModel} from "@contents/models/commonModel";
import {isEmptyString} from "@utils/commonUtils";

const SpaceRepository = () => {
    const queryClient = useQueryClient();

    const [_, setSpaceName] = useRecoilState(spaceNameState);
    const [spaceInfoState, setSpaceInfoState] = useRecoilState(spaceState);

    const [isLoading, setIsLoading] = useRecoilState(loadingState);

    // space list fetch
    const [fetchSpaceListData, spaceListData] = useLazyQuery<SpaceListGetPayload>(KEYS.QUERY.space.list, SpaceListGetApi);
    // space fetch
    const [fetchSpaceData, spaceData] = useLazyQuery<SpaceGetPayload>(KEYS.QUERY.space.item, SpaceGetApi);
    // space brand list fetch
    const [fetchSpaceBrandListData, spaceBrandListData] = useLazyQuery<SpaceBrandGetPayload>(KEYS.QUERY.space.popup, SpaceBrandGetApi);
    // space All list fetch
    const [fetchSpaceAllListData, spaceAllListData] = useLazyQuery<SpaceAllListGetPayload>(KEYS.QUERY.space.allList, SpaceAllListGetApi);
    // space brand shop list fetch
    const [fetchSpaceBrandShopAllListData, spaceBrandShopAllListData] = useLazyQuery<SpaceBrandShopAllListGetPayload>(KEYS.QUERY.space.brandShop, SpaceBrandShopAllListGetApi);

    // all space brand shop list fetch
    const [fetchAllSpaceBrandShopListData, allSpaceBrandShopListData] = useLazyQuery<AllSpaceBrandShopListGetPayload>(KEYS.QUERY.space.target, AllSpaceBrandShopListGetApi);

    // add space
    const [spaceAddMutate] = useCustomMutation<SpacePayload>(SpacePostApi);
    // edit space
    const [spaceEditMutate] = useCustomMutation<SpacePayload>(SpacePutApi);
    // delete space
    const [spaceDeleteMutate] = useCustomMutation<DeletePayload>(SpaceDeleteApi);

    // shop copy
    const [shopCopyMutate] = useCustomMutation<ShopCopyPayload>(ShopCopyApi);
    // shop copy progress
    const [fetchShopCopyProgressData, shopCopyProgressData, shopCopyProgressResult] = useLazyQuery<ShopCopyProgressPayload>(KEYS.QUERY.space.shopCopyProgress, ShopCopyProgressApi);
    const [isShopCopied, setIsShopCopied] = useRecoilState(isShopCopyState);

    const _invalidCaches = async () => {
        await queryClient.invalidateQueries({
            queryKey: KEYS.QUERY.space.list
        })
    }

    // call space list
    const getSearchSpaceList = ({spaceName, page, size, sort}: SpaceListGetPayload) => {
        const params: SpaceListGetPayload = {
            page,
            size,
            sort,
            spaceName,
        }
        fetchSpaceListData(params);
    }

    // call space brand popup list
    const getSpaceBrandPopupList = () => {
        const params: SpaceBrandGetPayload = {}
        fetchSpaceBrandListData(params);
    }

    // call all space brand shop list
    const getAllSpaceBrandShopList = () => {
        const params: AllSpaceBrandShopListGetPayload = {}
        fetchAllSpaceBrandShopListData(params);
    }

    const getSpaceById = ({id}: SpaceGetPayload) => {
        const params: SpaceGetPayload = {
            id
        }
        fetchSpaceData(params);
    }

    const getSpaceAllList = (callback?: (data: ResponseListEntity<SpaceEntity>) => void) => {
        const params: SpaceAllListGetPayload = {
            callback: (data: ResponseListEntity<SpaceEntity>) => callback && callback(data),
        }
        fetchSpaceAllListData(params);
    }

    const getSpaceBrandShopAllList = async ({spaceCode, callback}: SpaceBrandShopAllListGetPayload) => {
        const params: SpaceBrandShopAllListGetPayload = {
            spaceCode,
            callback
        }

        fetchSpaceBrandShopAllListData(params, spaceCode);
    }

    const onAddSpace = (data: SpaceEntity) => {
        const params: SpacePayload = {
            ...data,
            callback: _invalidCaches
        }
        spaceAddMutate(params)
    }

    const onEditSpace = (data: SpaceEntity) => {
        const params: SpacePayload = {
            ...data,
            callback: _invalidCaches
        }
        spaceEditMutate(params)
    }

    const onDeleteSpace = (spaceIds: Array<number>) => {
        spaceDeleteMutate(spaceIds);
    }

    const onSpaceSetRecoil = (callback?: () => void) => {
        setSpaceInfoState((spaceInfoState) => {
            return  {
                ...spaceInfoState,
                selectedOperation: 'OP',
                selectedTopMenu: 'op',
                selectedSystem: 'SPACE'
            }
        });
        callback && callback();
    }

    const onSetSpaceName = (space: string, callback?: () => void) => {
        setSpaceName(space);
        sessionStorage.setItem(KEYS.SPACE_NAME, space);
        callback && callback();
    }

    const onShopCopy = (shopCopySaveData: ShopCopySaveModel, shopCopyPayload: ShopCopyPayload) => {
        setIsLoading(true);
        const data: ShopCopyPayload = {
            ...shopCopyPayload,
            sourceShop: shopCopySaveData?.sourceShopId,
            targetShop: shopCopySaveData?.targetShopId,
            callback: (res: ResponseEntity<ShopCopyEntity>, variables: any, errorResponse: ResponseEntity<ShopCopyEntity>) => {
                setIsLoading(false);
                if(res?.content?.uuid){
                    //progress uuid localStorage 저장
                    const savedData = {
                        ...shopCopySaveData,
                        uuid: res?.content?.uuid || ''
                    }
                    setIsShopCopied(true);
                    localStorage.setItem(KEYS.SHOP_COPY, JSON.stringify(savedData));
                }

                if(errorResponse){
                    shopCopyPayload?.callback && shopCopyPayload?.callback(errorResponse);
                }else{
                    shopCopyPayload?.callback && shopCopyPayload?.callback(res);
                }
            }
        }
        shopCopyMutate(data);
    }

    const getShopCopyProgress = (callback?: (...args: any[]) => void) => {
        const shopCopyData = localStorage.getItem(KEYS.SHOP_COPY) || '';
        if(isEmptyString(shopCopyData)){
            setIsShopCopied(false);
            return;
        }

        const uuid = (JSON.parse(shopCopyData) as ShopCopySaveModel)?.uuid;
        if(uuid){
            const data: ShopCopyProgressPayload = {
                uuid: uuid,
                callback: (res: ResponseEntity<ShopCopyProgressEntity>) => {
                    callback && callback(res?.content);
                }
            }
            fetchShopCopyProgressData(data);
        }
    }

    return {
        spaceListData,
        spaceData,
        spaceBrandListData,
        spaceAllListData,
        spaceBrandShopAllListData,
        allSpaceBrandShopListData,
        getSearchSpaceList,
        getSpaceById,
        onAddSpace,
        onEditSpace,
        onDeleteSpace,
        getSpaceBrandPopupList,
        getSpaceAllList,
        getSpaceBrandShopAllList,
        onSpaceSetRecoil,
        onSetSpaceName,
        getAllSpaceBrandShopList,
        onShopCopy,
        getShopCopyProgress,
        shopCopyProgressData,
        shopCopyProgressResult
    }
}

export default SpaceRepository;
